import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

import { useDeleteNotificationTemplateMutation } from '../../../../services/redux/api/notificationsAdminApi';
import { INotificationTemplate } from '../../../../types/interfaces/notification-admin';
import useFlexibleColumnsTable from '../../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../../shared/settingsNames';
import getNotificationTemplatesTableColumns, {
  getNotificationTemplatesDefaultSettings,
} from './notificationTemplatesTableColumns';
import {
  notificationCategoryLabel,
  notificationPreferenceGroupLabel,
} from '../../../../utils/notification-template-labels';
import { getNotificationTemplatesCellReducers } from './getNotificationTemplatesCellReducers';

export function useNotificationTemplatesListComponent(
  classes: Record<string, string>,
  templates: INotificationTemplate[],
  onOpenRow: (row: INotificationTemplate) => void,
  canDelete: boolean,
) {
  const includeActionsColumn = canDelete;
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [itemForDelete, setItemForDelete] =
    useState<INotificationTemplate | null>(null);

  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      active: allOption,
      category: allOption,
      preferenceGroup: allOption,
    },
    onSubmit: () => {},
  });

  const filteredValues = useMemo(
    () =>
      templates.filter((row) => {
        const fv = filter.toLowerCase();
        if (fv && !row.code.toLowerCase().includes(fv)) {
          return false;
        }
        const activeRaw = filterFormik.values.active?.id;
        if (typeof activeRaw === 'boolean') {
          if (activeRaw !== !!row.active) {
            return false;
          }
        }
        const catId = filterFormik.values.category?.id ?? 'all';
        if (catId !== 'all' && row.category !== catId) return false;
        const prefId = filterFormik.values.preferenceGroup?.id ?? 'all';
        if (prefId !== 'all' && row.preferenceGroup !== prefId) return false;
        return true;
      }),
    [templates, filter, filterFormik.values],
  );

  const { order: defaultOrder, orderBy } =
    getNotificationTemplatesDefaultSettings(t, includeActionsColumn);

  const cellReducers = getNotificationTemplatesCellReducers(
    t,
    classes,
    { canDelete },
    onOpenRow,
    (row) => setItemForDelete(row),
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.NOTIFICATION_TEMPLATES_TABLE_SETTINGS,
    getNotificationTemplatesTableColumns(t, includeActionsColumn),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  const [deleteTemplate] = useDeleteNotificationTemplateMutation();

  function onItemDelete() {
    if (!itemForDelete?.id) return;
    deleteTemplate(itemForDelete.id);
    setItemForDelete(null);
  }

  const filterActiveOptions = [
    allOption,
    { id: true, name: t('common.active') },
    { id: false, name: t('common.notActive') },
  ];

  const categoryOptions = [
    allOption,
    {
      id: 'marketing',
      name: notificationCategoryLabel(t, 'marketing'),
    },
    {
      id: 'transactional',
      name: notificationCategoryLabel(t, 'transactional'),
    },
  ];

  const preferenceOptions = [
    allOption,
    {
      id: 'system',
      name: notificationPreferenceGroupLabel(t, 'system'),
    },
    {
      id: 'social',
      name: notificationPreferenceGroupLabel(t, 'social'),
    },
    {
      id: 'marketing',
      name: notificationPreferenceGroupLabel(t, 'marketing'),
    },
    {
      id: 'reminder',
      name: notificationPreferenceGroupLabel(t, 'reminder'),
    },
  ];

  return {
    sortedArray,
    columnsList,
    columnsListForPopup,
    order,
    orderBy,
    cellReducers,
    onSort,
    onSortColumns,
    onToggleHideColumn,
    onRestoreDefault,
    filter,
    setFilter,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filterFormik,
    filterActiveOptions,
    categoryOptions,
    preferenceOptions,
  };
}
